import moment from 'moment'
import { useMemo } from 'react'
import { MatchedEntity } from '../../../@types/GetEntitiesDetails'
import {
  Pet,
  Questionnaire,
  RunPetisSimulationClientRequest,
} from '../../../@types/RunPetisSimulation'
import { orderAnimalAddress } from '../../../Components/AnimalInsuranceTable/CreateAddress'
import { ENV } from '../../../Environment'
import { useAnimalStore } from '../../../Stores/animalStore'
import { useSimulatorStore } from '../../../Stores/simulatorStore'
import { useAnimalValidateStore } from '../../../Stores/useAnimalValidateStore'
import { useFillDataForSimulationStore } from '../../../Stores/useFillDataForSimulationStore'
import { useRunPetisSimulationStore } from '../../../Stores/useRunPetisSimulationStore'
import { AnimalsSecured } from '../../../Types/AnimalStore'
import { concatenateObjectValuesWithoutComma } from '../../../utils/address'
import { truncateAddress } from '../../../utils/streetNameFormatted'

export const useRunPetisSimulationPayload = () => {
  const { animalsList } = useAnimalStore()
  const { entitySelected, entity, initialDate } = useSimulatorStore()
  const { fillDataForSimulationRequest } = useFillDataForSimulationStore()
  const { runPetisSimulation } = useRunPetisSimulationStore()
  const { addressOwner } = useAnimalValidateStore()

  const _entitySelected = entitySelected as MatchedEntity

  const mountQuestionary = (animal: AnimalsSecured) => {
    const questionary: Questionnaire = {
      sections: animal?.questionary?.map((section) => ({
        code: section.id.toString(),
        title: section.title,
        questions: section?.questions?.map((question) => {
          const questionAnswer =
            (question.answer || '')?.length > 3
              ? concatenateObjectValuesWithoutComma(
                  truncateAddress(
                    addressOwner?.[`address-${animal.id}`] as never,
                    'entityComplement',
                  ) as any,
                  orderAnimalAddress,
                )
              : question.answer?.toLocaleUpperCase()

          return {
            code: question.id,
            id: question.id as string,
            order: question.order,
            questionPhrase: question.question,
            conditions: question.conditions || [],
            Answers: [
              {
                ResponseTypeCode: question.responseTypeCode,
                AnswerPhrase: questionAnswer || '',
              },
            ],
          }
        }),
      })),
    }
    return questionary
  }

  const runPetisSimulationPayload: RunPetisSimulationClientRequest =
    useMemo<RunPetisSimulationClientRequest>(
      () => ({
        agentDetailsRequest: fillDataForSimulationRequest?.agentDetailsResponse,
        runPetisSimulationRequest: {
          quoteId:
            runPetisSimulation?.runPetisSimulationResponse?.quoteId || '',
          startDate: moment(initialDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          entityData: {
            address: {
              postalCode: entitySelected?.addresses?.[0]?.postalCode || null,
              addressPointer: null,
            },
            birthDate:
              entitySelected?.birthdate &&
              moment(entitySelected?.birthdate).isValid()
                ? moment(entitySelected?.birthdate).format()
                : null,
            contactDetails: {
              phonePointer: _entitySelected?.entityPhone || null,
              mailingAddressPointer: null,
            },
            name: entitySelected?.name,
            entityId: entitySelected?.idEntity,
            idExternal: entitySelected?.idEntity,
            idExternalType: entitySelected?.idExternalType,
            endDate: entity?.endDate,
            initialDate: entity?.initialDate,
            payer: entity?.payer,
            role: entity?.role,
          },
          petsList: animalsList.map(
            (item, index): Pet => ({
              id: item.id.toString(),
              name: item.name || `Animal${index + 1}`,
              birthDate: item?.birthDate,
              species: item?.species,
              speciesCode: item?.speciesCode,
              breed: item?.breed,
              breedCode: item?.breedCode,
              questionnaire: mountQuestionary(item),
            }),
          ),
        },
        productCode: `${ENV().ProductCode}${ENV().ProductCodeVersion}`,
        coreId: ENV().CoreSystem,
        protocolCode: '0', //TODO: verificar este dado, onde pegar esse valor?
        operationType: 'E', //TODO: verificar este dado, onde pegar esse valor?
      }),
      [],
    )

  return {
    runPetisSimulationPayload,
  }
}
